/**
 * CrossSupplierActionPanel — Iter 157 (Phase 5)
 *
 * Surfaces the top urgent actions across ALL connected suppliers in one
 * panel on the buyer dashboard. Each action is a 1-click deep-link that
 * auto-switches the buyer's supplier context + opens the right page.
 *
 *   "Pay $250 invoice INV-1234 to Acme · 3 days overdue → [Pay now]"
 *   "Book carrier for Order PO-2024-0091 from BetaCo → [Book]"
 *   "Schedule delivery for shipment ZX1Y2 from GammaCo → [Track]"
 *
 * Used inside /buyer dashboard. Powered by the same /api/orders +
 * /api/invoices + /api/shipping endpoints that already return data
 * across all connected suppliers (no new backend needed).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money } from "../lib/api";
import { useSupplier } from "../context/SupplierContext";
import { toast } from "sonner";
import {
  CreditCard, Truck, Repeat, AlertTriangle, ArrowRight, Zap, Sparkles, Store,
} from "lucide-react";

const MAX_ROWS = 5;

export default function CrossSupplierActionPanel() {
  const nav = useNavigate();
  const { connections, selectSupplier, activeSellerId } = useSupplier();
  const [data, setData] = useState({ orders: [], invoices: [], shipments: [] });
  const [loaded, setLoaded] = useState(false);

  // Don't render once a supplier is in scope — only useful in Hub mode.
  const inHubMode = !activeSellerId;

  useEffect(() => {
    if (!inHubMode) return;
    Promise.all([
      api.get("/orders").catch(() => ({ data: [] })),
      api.get("/invoices").catch(() => ({ data: [] })),
      api.get("/shipping").catch(() => ({ data: [] })),
    ]).then(([o, i, s]) => {
      setData({ orders: o.data || [], invoices: i.data || [], shipments: s.data || [] });
    }).finally(() => setLoaded(true));
  }, [inHubMode]);

  const connectionsBySellerId = useMemo(() => {
    const m = new Map();
    (connections || []).forEach(c => c.tenant_owner_user_id && m.set(c.tenant_owner_user_id, c));
    return m;
  }, [connections]);

  const actions = useMemo(() => {
    if (!loaded) return [];
    const out = [];
    const now = Date.now();
    // (1) Unpaid invoices · highest urgency first (overdue ↑)
    for (const inv of data.invoices) {
      if (inv.status === "paid" || inv.status === "draft" || inv.status === "void") continue;
      const due = inv.due_date ? new Date(inv.due_date).getTime() : null;
      const overdueDays = due ? Math.max(0, Math.floor((now - due) / 86400000)) : 0;
      out.push({
        kind:       "pay",
        priority:   overdueDays > 0 ? 100 + overdueDays : 50,
        sellerId:   inv.seller_id,
        sellerName: inv.seller_name,
        code:       inv.code,
        amount:     inv.amount,
        overdueDays,
        action: () => {
          const conn = connectionsBySellerId.get(inv.seller_id);
          if (conn) selectSupplier(conn);
          nav(`/buyer/payments?pay=${inv.id}&mode=now#wallet`);
        },
      });
    }
    // (2) Orders ready for pickup · need carrier booking
    for (const o of data.orders) {
      if (o.status !== "ready_for_pickup") continue;
      out.push({
        kind:       "ship",
        priority:   70,
        sellerId:   o.seller_id,
        sellerName: o.seller_name,
        code:       o.code,
        action: () => {
          const conn = connectionsBySellerId.get(o.seller_id);
          if (conn) selectSupplier(conn);
          nav("/buyer/shipping");
        },
      });
    }
    // (3) Shipments out_for_delivery · keep them top-of-mind
    for (const s of data.shipments) {
      if (s.status !== "out_for_delivery") continue;
      const order = data.orders.find(o => o.id === s.order_id);
      out.push({
        kind:       "track",
        priority:   30,
        sellerId:   order?.seller_id,
        sellerName: order?.seller_name || s.seller_name,
        code:       s.tracking,
        action: () => {
          const conn = connectionsBySellerId.get(order?.seller_id);
          if (conn) selectSupplier(conn);
          nav("/buyer/shipping");
        },
      });
    }
    return out.sort((a, b) => b.priority - a.priority).slice(0, MAX_ROWS);
  }, [loaded, data, connectionsBySellerId, selectSupplier, nav]);

  if (!inHubMode) return null;
  if (loaded && actions.length === 0) return null;

  return (
    <div
      className="rounded-2xl border-2 border-amber-200 bg-amber-50/30 p-5"
      data-testid="cross-supplier-action-panel"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-amber-100 text-amber-700 grid place-items-center">
          <Zap className="w-3.5 h-3.5"/>
        </div>
        <div>
          <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-amber-800">
            Action needed across suppliers
          </div>
          <div className="text-[12.5px] text-foreground/70">
            We&apos;ll auto-switch you to the right supplier when you click.
          </div>
        </div>
      </div>
      {!loaded ? (
        <div className="text-[12.5px] text-foreground/55 py-3">Scanning your suppliers…</div>
      ) : (
        <ul className="space-y-2">
          {actions.map((a, i) => (
            <li
              key={`${a.kind}-${a.code}-${i}`}
              className="flex items-center gap-3 rounded-xl bg-white border border-border p-3"
              data-testid={`xsupplier-action-${a.kind}-${i}`}
            >
              <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${
                a.kind === "pay"   ? "bg-rose-50 text-rose-700"   :
                a.kind === "ship"  ? "bg-amber-50 text-amber-700" :
                                      "bg-indigo-50 text-indigo-700"
              }`}>
                {a.kind === "pay"  ? <CreditCard className="w-4 h-4"/> :
                 a.kind === "ship" ? <Truck className="w-4 h-4"/>      :
                                     <Sparkles className="w-4 h-4"/>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium leading-tight">
                  {a.kind === "pay"   && <>Pay {money(a.amount)} · {a.code}</>}
                  {a.kind === "ship"  && <>Book carrier for {a.code}</>}
                  {a.kind === "track" && <>Out for delivery · {a.code}</>}
                </div>
                <div className="text-[11.5px] text-foreground/55 flex items-center gap-1.5 mt-0.5">
                  <Store className="w-2.5 h-2.5"/>
                  <span className="truncate">{a.sellerName || "Supplier"}</span>
                  {a.overdueDays > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-rose-700 mono ml-2">
                      <AlertTriangle className="w-2.5 h-2.5"/>{a.overdueDays}d overdue
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => { a.action(); toast.success(`Switched to ${a.sellerName || "supplier"}`); }}
                className="shrink-0 inline-flex items-center gap-1 rounded-full bg-foreground text-background text-[12px] mono uppercase tracking-wider px-3.5 py-1.5 hover:opacity-90"
                data-testid={`xsupplier-cta-${a.kind}-${i}`}
              >
                {a.kind === "pay" ? "Pay" : a.kind === "ship" ? "Book" : "Track"}
                <ArrowRight className="w-3 h-3"/>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
